import { cn } from "@/lib/utils";

/**
 * Escala vertebral — elemento de assinatura do sistema.
 *
 * Vem da coluna de esferas decrescentes do símbolo da marca (ver RunnerMark em
 * components/brand/BrandLogo.js), que é uma coluna vertebral estilizada. Aqui
 * ela codifica PROGRESSÃO, nunca decora: quantos segmentos estão cheios diz o
 * nível, e o afunilamento diz o sentido (cheio → esgotando).
 *
 * Usar onde havia só um número colorido solto — nível de estoque, etapa de OS,
 * avanço de meta. O número continua ao lado: a escala é leitura periférica, para
 * quem varre uma tabela de 40 linhas; o número é a leitura exata.
 */

/* Diâmetros em px, na mesma proporção das esferas do símbolo. */
const SEGMENTS = [7, 6.2, 5.4, 4.6, 3.8, 3];

export default function Scale({
  value = 0,
  max = 10,
  label,
  showValue = true,
  className,
}) {
  const ratio = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
  const filled = value <= 0 ? 0 : Math.max(1, Math.ceil(ratio * SEGMENTS.length));

  // Mesma regra de cor das tabelas de estoque: 0 = falha, baixo = atenção,
  // saudável = teal (progressão).
  const tone = value <= 0 ? "bg-red-500" : ratio <= 0.5 ? "bg-amber-400" : "bg-teal-500";
  const numberTone = value <= 0 ? "text-red-600" : ratio <= 0.5 ? "text-amber-600" : "text-slate-900";

  return (
    <span
      className={cn("inline-flex items-center gap-2", className)}
      role="img"
      aria-label={label || `${value} de ${max}`}
    >
      <span className="inline-flex items-center gap-[3px]" aria-hidden>
        {SEGMENTS.map((d, i) => (
          <span
            key={i}
            style={{ width: d, height: d }}
            className={cn("rounded-full shrink-0 transition-colors", i < filled ? tone : "bg-slate-200")}
          />
        ))}
      </span>
      {showValue && (
        <span className={cn("font-mono text-sm font-semibold tabular-nums", numberTone)} aria-hidden>
          {value}
        </span>
      )}
    </span>
  );
}
